import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

const STEP_ROUTES = {
  company_profile: '/app/broker/admin/organization',
  first_user: '/app/broker/admin/users',
  first_carrier: '/app/broker/carriers/create',
  first_customer: '/app/broker/customers',
  first_load: '/app/broker/create',
  integrations: '/app/broker/sap-settings'
};

const STEP_ICONS = {
  company_profile: '🏢',
  first_user: '👥',
  first_carrier: '🚛',
  first_customer: '🏭',
  first_load: '📦',
  integrations: '🔗'
};

export default function OnboardingWizard({ onDismiss }) {
  const navigate = useNavigate();
  const { adminEmail } = useAuth();
  const [progress, setProgress] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    loadProgress();
  }, []);

  async function loadProgress() {
    try {
      const res = await fetch('/api/onboarding/progress', {
        headers: { 'x-admin-email': adminEmail }
      });
      const data = await res.json();
      setProgress(data.progress);
      setSteps(data.steps || []);
      
      // If complete, don't show
      if (data.progress?.isComplete) {
        onDismiss?.();
      }
    } catch (e) {
      console.error('Load onboarding error:', e);
    } finally {
      setLoading(false);
    }
  }

  async function dismissOnboarding() {
    try {
      await fetch('/api/onboarding/dismiss', {
        method: 'POST',
        headers: { 'x-admin-email': adminEmail }
      });
      onDismiss?.();
    } catch (e) {
      console.error('Dismiss error:', e);
    }
  }

  function goToStep(stepKey) {
    const route = STEP_ROUTES[stepKey];
    if (route) navigate(route);
  }

  if (loading || !progress || progress.isComplete) return null;

  const completedSteps = progress.completedSteps || [];
  const percentComplete = Math.round((completedSteps.length / steps.length) * 100);

  if (!expanded) {
    return (
      <div 
        onClick={() => setExpanded(true)}
        style={{ 
          position: 'fixed', 
          bottom: 90, 
          right: 24, 
          background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
          color: 'white',
          padding: '12px 20px',
          borderRadius: 50,
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          zIndex: 999
        }}
      >
        <span>🚀</span>
        <span style={{ fontWeight: 700 }}>{percentComplete}% Setup Complete</span>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'fixed', 
      bottom: 90, 
      right: 24, 
      width: 360, 
      background: 'white', 
      borderRadius: 16, 
      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
      overflow: 'hidden',
      zIndex: 999
    }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)', 
        padding: 20, 
        color: 'white' 
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>🚀 Getting Started</div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>Complete these steps to set up RoadOptix</div>
          </div>
          <button 
            onClick={() => setExpanded(false)}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, padding: '4px 8px', color: 'white', cursor: 'pointer' }}
          >
            —
          </button>
        </div>
        
        {/* Progress bar */}
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
            <span>{completedSteps.length} of {steps.length} complete</span>
            <span>{percentComplete}%</span>
          </div>
          <div style={{ height: 8, background: 'rgba(255,255,255,0.3)', borderRadius: 4 }}>
            <div style={{ height: '100%', width: `${percentComplete}%`, background: 'white', borderRadius: 4, transition: 'width 0.3s' }} />
          </div>
        </div>
      </div>

      {/* Steps */}
      <div style={{ padding: 16, maxHeight: 300, overflowY: 'auto' }}>
        {steps.map((step, i) => {
          const isComplete = completedSteps.includes(step.key);
          const icon = STEP_ICONS[step.key] || '📌';
          
          return (
            <div 
              key={step.key}
              onClick={() => !isComplete && goToStep(step.key)}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 12, 
                padding: 12, 
                borderRadius: 8,
                marginBottom: 8,
                background: isComplete ? '#f0fdf4' : '#f9fafb',
                border: isComplete ? '1px solid #bbf7d0' : '1px solid #e5e7eb',
                cursor: isComplete ? 'default' : 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ 
                width: 36, 
                height: 36, 
                borderRadius: '50%', 
                background: isComplete ? '#10b981' : '#e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isComplete ? 16 : 18
              }}>
                {isComplete ? '✓' : icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: isComplete ? '#065f46' : '#111827' }}>
                  {step.title}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{step.description}</div>
              </div>
              {!isComplete && (
                <div style={{ color: '#3b82f6', fontSize: 18 }}>→</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ padding: 16, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button 
          onClick={dismissOnboarding}
          style={{ fontSize: 13, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          Skip for now
        </button>
        {percentComplete === 100 && (
          <button 
            onClick={dismissOnboarding}
            style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
          >
            Complete Setup 🎉
          </button>
        )}
      </div>
    </div>
  );
}
